// ═══════════════════════════════════════════════════════════════════════════
// RUN 22: Add Confidential to Footers
// ═══════════════════════════════════════════════════════════════════════════
import { safeAlert, reportResult, paraDetail, RunDetail } from '../runs/common';
import { getConfig, ConfidentialConfig } from '../taskpane/configService';

// ── Helpers (extracted to reduce cognitive complexity) ───────────────────

/** Returns true if the document has the even/odd pages header setting enabled. */
async function probeEvenAndOdd(
  context: Word.RequestContext,
  sections: Word.SectionCollection,
): Promise<boolean> {
  try {
    const ooxml = sections.items[0].body.getOoxml();
    await context.sync();
    return /<w:evenAndOddHeaders[\s/>]/.test(ooxml.value);
  } catch (e) {
    console.log(`  Could not probe evenAndOddHeaders setting: ${(e as Error).message}`);
    return false;
  }
}

/** Returns true if the given section has a title page footer. */
async function probeTitlePage(
  context: Word.RequestContext,
  section: Word.Section,
  sectionIndex: number,
): Promise<boolean> {
  try {
    const ooxml = section.body.getOoxml();
    await context.sync();
    return /<w:titlePg[\s/>]/.test(ooxml.value);
  } catch (e) {
    console.log(`  Section ${sectionIndex + 1}: Could not probe titlePg: ${(e as Error).message}`);
    return false;
  }
}

/** Builds the list of footer types that Word will actually render for a section. */
function activeFooterTypes(
  hasTitlePg: boolean,
  evenAndOddEnabled: boolean,
): { type: Word.HeaderFooterType; name: string }[] {
  const types: { type: Word.HeaderFooterType; name: string }[] = [
    { type: Word.HeaderFooterType.primary, name: 'Primary' },
  ];
  if (hasTitlePg)        types.push({ type: Word.HeaderFooterType.firstPage, name: 'FirstPage' });
  if (evenAndOddEnabled) types.push({ type: Word.HeaderFooterType.evenPages, name: 'EvenPages' });
  return types;
}

interface FooterStats {
  footerStyleFixed: number;
  confidentialAdded: number;
  alreadyPresent: number;
  navDetails: RunDetail[];
}

/**
 * Processes a single footer body:
 * - Applies the configured style to every existing paragraph that has text.
 * - Inserts the confidential paragraph at the end if not already present.
 */
async function processFooterBody(
  context: Word.RequestContext,
  footerBody: Word.Body,
  c: ConfidentialConfig,
  sectionLabel: string,
): Promise<Pick<FooterStats, 'footerStyleFixed' | 'confidentialAdded' | 'alreadyPresent' | 'navDetails'>> {
  let footerStyleFixed = 0;
  let confidentialAdded = 0;
  let alreadyPresent = 0;
  const navDetails: RunDetail[] = [];

  const paragraphs = footerBody.paragraphs;
  paragraphs.load('items');
  await context.sync();

  console.log(`\n── ${sectionLabel} (${paragraphs.items.length} para(s)) ──`);

  let hasConfidential = false;

  for (let p = 0; p < paragraphs.items.length; p++) {
    const para = paragraphs.items[p];
    para.load('text, style, uniqueLocalId');
    await context.sync();

    const paraText = (para.text || '').trim();

    if (paraText.toLowerCase().includes(c.confidentialText.toLowerCase())) {
      hasConfidential = true;
      console.log(`  [P${p + 1}] "${paraText.substring(0, 50)}" — ${c.confidentialText} found ✓`);
    }

    // Apply footer paragraph style to all existing paragraphs that have text
    const currentStyle = (para.style || '').toLowerCase();
    if (currentStyle !== c.confistyle.toLowerCase() && paraText.length > 0) {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      para.style = c.confistyle;
      await context.sync();
      footerStyleFixed++;
      navDetails.push(paraDetail(para.uniqueLocalId, paraText, `→ ${c.confistyle}`));
      console.log(`  [P${p + 1}] "${paraText.substring(0, 40)}" pStyle → "${c.confistyle}" ✓`);
    }
  }

  if (hasConfidential) {
    alreadyPresent++;
    console.log(`  ${c.confidentialText} already present — no insertion needed`);
    return { footerStyleFixed, confidentialAdded, alreadyPresent, navDetails };
  }

  console.log(`  Inserting "${c.confidentialText}" paragraph...`);

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();

  const confPara = footerBody.insertParagraph(c.confidentialText, Word.InsertLocation.end);
  confPara.style       = c.confistyle;
  confPara.alignment   = c.alignment as Word.Alignment;
  confPara.font.bold   = c.bold;
  confPara.load('uniqueLocalId');
  await context.sync();

  confidentialAdded++;
  navDetails.push(paraDetail(confPara.uniqueLocalId, c.confidentialText, `inserted → ${c.confistyle}`));
  console.log(`  "${c.confidentialText}" inserted (${c.alignment}, bold=${c.bold}, ${c.confistyle} style) ✓`);

  return { footerStyleFixed, confidentialAdded, alreadyPresent, navDetails };
}

// ── Main export ──────────────────────────────────────────────────────────

export async function run22() {
  // ── Read config ──
  const cfg = getConfig();
  const c = cfg.run22_addConfidential;

  await Word.run(async (context) => {
    try {
      console.log('═'.repeat(70));
      console.log('RUN 22: Add Confidential to Footers');
      console.log('═'.repeat(70));

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const sections = context.document.sections;
      sections.load('items');
      await context.sync();

      const totalSections = sections.items.length;
      console.log(`Total sections: ${totalSections}\n`);

      const stats: FooterStats = {
        footerStyleFixed: 0,
        confidentialAdded: 0,
        alreadyPresent: 0,
        navDetails: [],
      };

      const evenAndOddEnabled = await probeEvenAndOdd(context, sections);
      console.log(`Document odd/even pages setting: ${evenAndOddEnabled ? 'ON' : 'OFF'}`);

      for (let s = 0; s < totalSections; s++) {
        const section    = sections.items[s];
        const hasTitlePg = await probeTitlePage(context, section, s);
        const footerList = activeFooterTypes(hasTitlePg, evenAndOddEnabled);

        console.log(
          `  Section ${s + 1}: titlePg=${hasTitlePg} → processing ${footerList.map(f => f.name).join(', ')}`,
        );

        for (const ft of footerList) {
          let footerBody: Word.Body;
          try {
            footerBody = section.getFooter(ft.type);
            footerBody.load('paragraphs');
            await context.sync();
          } catch (e) {
            // Footer type not present in this section — skip it
            console.log(`  Section ${s + 1} ${ft.name}: No footer — skipped (${(e as Error).message})`);
            continue;
          }

          const label  = `Section ${s + 1} ${ft.name} Footer`;
          const result = await processFooterBody(context, footerBody, c, label);

          stats.footerStyleFixed  += result.footerStyleFixed;
          stats.confidentialAdded += result.confidentialAdded;
          stats.alreadyPresent    += result.alreadyPresent;
          stats.navDetails.push(...result.navDetails);
        }
      }

      // ── Summary ────────────────────────────────────────────────────────
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      const totalFixes = stats.footerStyleFixed + stats.confidentialAdded;

      console.log('\n' + '═'.repeat(70));
      console.log(`Footer styles fixed: ${stats.footerStyleFixed}`);
      console.log(`${c.confidentialText} added: ${stats.confidentialAdded}`);
      console.log(`Already present: ${stats.alreadyPresent}`);
      console.log(`Total fixes: ${totalFixes}`);
      console.log('✓ Add Confidential check complete!');
      console.log('═'.repeat(70));

      if (totalFixes > 0) {
        reportResult('Add Confidential Complete', [
          { label: `Footer paragraphs styled → ${c.confistyle}`, count: stats.footerStyleFixed },
          { label: `${c.confidentialText} added`,                count: stats.confidentialAdded },
          { label: 'Already present',                            count: stats.alreadyPresent },
          ...stats.navDetails,
        ]);
      } else {
        // No changes made — emit a zero-change summary so Run 22 still appears
        // in the QC Summary panel with its expand arrow.
        reportResult('Add Confidential — no changes needed', [
          { label: `All footers already contain "${c.confidentialText}"`, note: 'No changes made' },
          { label: 'Footer paragraphs styled', count: 0 },
          { label: `${c.confidentialText} added`,  count: 0 },
          { label: 'Already present',              count: stats.alreadyPresent },
        ]);
      }

    } catch (error) {
      console.error('Error in run22:', error);
      console.error('Message:', (error as Error).message);
      safeAlert('An error occurred during Add Confidential.\nCheck console (F12) for details.');
    }
  });
}